/**
 * Dashboard executivo (brief 3.1, Task 10 · redesign Entrega 2).
 *
 * É a migração da tela de Command Center que antes era HTML renderizado a
 * partir de FIXTURE (`packages/ui/src/render.ts` → `renderCommandCenter`) para
 * React sobre DADOS REAIS. O contrato de dados continua o mesmo
 * (`CommandCenterStats`) e os indicadores continuam vindo da mesma função pura
 * (`computeCommandCenterStats`) — só a origem mudou: fixture → API.
 *
 * Redesign Entrega 2 (apresentação apenas): layout command-center. Grid de KPIs
 * no topo com hierarquia forte; o Command Center vira um painel único com uma
 * FAIXA de métricas separadas por divisores (não quatro sombras gratuitas) e um
 * indicador gráfico leve. `data-page`, `data-stat` (e valores), os textos dos
 * `dt` e `aria-label="Command Center"` são PRESERVADOS.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { KpiGrid } from "../components/KpiCard.js";
import { MiniBarChart, MiniColumnChart } from "../components/MiniChart.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { TopProductsPanel } from "../components/TopProductsPanel.js";
import { periodDays, type ResolvedPeriod } from "../period.js";
import { StatusPanel } from "../components/StatusPanel.js";
import {
  AWAITING_CONTRACT_LABEL,
  buildCommandCenterStats,
  buildDashboardKpis,
  type DashboardDataset
} from "../dashboard/kpis.js";
import type { DashboardViewProps } from "../dashboard/view-props.js";
import { formatBrl, formatCount, formatPercent } from "../format.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { revenueSummary, stateBuckets } from "../orders/list-view.js";
import { salesChannelBuckets } from "../orders/channels.js";
import { applyUnifiedOrderKpis } from "../orders/unified-kpis.js";
import { DAILY_SERIES_WINDOWS, dailySeries, type DailySeriesWindow } from "../orders/daily-series.js";
import { Card, CardHeader, EmptyState, PageHeader, Segmented } from "../ui/index.js";
import { AcquisitionLeaderCards } from "../components/AcquisitionPanels.js";
import { useSalesOrigins } from "../queries.js";
import type { SalesOriginOption } from "../orders/acquisition.js";

/** Métrica da faixa do Command Center: rótulo + valor, separados por divisor. */
function stat(label: string, statKey: string, value: string): ReactElement {
  return h(
    "div",
    {
      className:
        "flex flex-col gap-1 px-0 py-1 sm:px-4 sm:py-0 " +
        "sm:border-l sm:border-border first:sm:border-l-0"
    },
    h("dt", { className: "text-sm font-extrabold text-muted" }, label),
    h("dd", { className: "m-0 text-2xl font-black tabular-nums text-ink", "data-stat": statKey }, value)
  );
}

/**
 * Painéis de pedidos por estado e por canal (Fase 2 do redesign admin). Só
 * contam o que a fonte unificada publica; sem a rota, dizem que falta — nunca
 * um gráfico vazio que pareça "zero pedidos". Sem série por período: o modelo
 * não tem fonte histórica (ver MiniChart).
 */
function OrdersPanels({ orders }: { orders: OrdersDataset | undefined }): ReactElement {
  if (!orders || isOrdersSourceAwaitingContract(orders)) {
    return h(StatusPanel, {
      kind: "awaiting_source",
      title: `Pedidos por estado e por canal ${AWAITING_CONTRACT_LABEL}`,
      detail: "A rota /api/platform/orders não respondeu neste ambiente."
    });
  }
  const states = stateBuckets(orders.orders);
  const channels = salesChannelBuckets(orders.orders);
  return h(
    "div",
    { className: "grid grid-cols-1 gap-4 lg:grid-cols-2", "data-orders-panels": String(orders.orders.length) },
    h(
      Card,
      null,
      h(CardHeader, { title: "Pedidos por estado", description: "Quantos pedidos de todos os canais há em cada etapa." }),
      states.length
        ? h(MiniBarChart, {
            title: "Pedidos por estado",
            "data-chart": "pedidos-por-estado",
            bars: states.map((bucket) => ({
              label: bucket.label,
              value: bucket.count,
              display: formatCount(bucket.count),
              emphasis: bucket.key === "payment_pending"
            }))
          })
        : h(EmptyState, { title: "Nenhum pedido ainda" })
    ),
    h(
      Card,
      null,
      h(CardHeader, { title: "Participação por canal", description: "Receita de pedidos que valem venda, por canal de venda (Mercado Livre, Shopee, Magalu…)." }),
      channels.length
        ? h(MiniBarChart, {
            title: "Receita por canal",
            "data-chart": "receita-por-canal",
            bars: channels.map((bucket) => ({
              label: `${bucket.label} (${formatCount(bucket.count)})`,
              value: bucket.revenueCents,
              display: formatBrl(bucket.revenueCents)
            }))
          })
        : h(EmptyState, { title: "Nenhum pedido ainda" })
    )
  );
}

/**
 * Receita e pedidos por dia (C5), com janela de 7 ou 30 dias. A série é DERIVADA dos pedidos
 * (`orders/daily-series.ts`), não de uma tabela histórica: cobre só o que os pedidos existentes
 * cobrem e a nota abaixo do gráfico diz isso. Sem a fonte de pedidos, o painel principal já
 * declara a ausência (`OrdersPanels`), então este não é renderizado.
 */
function DailySeriesPanel({ orders, now, period }: { orders: OrdersDataset; now: Date; period?: ResolvedPeriod }): ReactElement {
  const [windowDays, setDays] = useState<DailySeriesWindow>(7);
  // Com período escolhido na barra, a série cobre exatamente esse período (até 62 dias); sem ele, mantém a janela 7/30.
  const days = period ? Math.min(periodDays(period), 62) : windowDays;
  const anchor = period ? new Date(`${period.to}T15:00:00-03:00`) : now;
  const series = dailySeries(orders.orders, days, anchor);
  const total = series.reduce((sum, point) => sum + point.revenueCents, 0);
  const count = series.reduce((sum, point) => sum + point.orders, 0);
  return h(
    Card,
    null,
    h(CardHeader, {
      title: "Receita por dia",
      description: period ? `${formatBrl(total)} em ${formatCount(count)} pedido(s) no período.` : `${formatBrl(total)} em ${formatCount(count)} pedido(s) nos últimos ${days} dias.`,
      actions: period ? undefined : h(Segmented<`${DailySeriesWindow}`>, {
        label: "Período",
        value: `${windowDays}` as `${DailySeriesWindow}`,
        options: DAILY_SERIES_WINDOWS.map((window) => ({ value: `${window}` as `${DailySeriesWindow}`, label: `${window} dias` })),
        onChange: (value) => setDays(Number(value) as DailySeriesWindow)
      })
    }),
    count === 0
      ? h(EmptyState, { title: "Nenhum pedido neste período" })
      : h(MiniColumnChart, {
          title: period ? `Receita por dia, ${days} dia(s) do período` : `Receita por dia, últimos ${days} dias`,
          "data-chart": "receita-por-dia",
          columns: series.map((point) => ({ label: point.label, value: point.revenueCents, display: formatBrl(point.revenueCents) }))
        }),
    h(
      "p",
      { className: "m-0 mt-2 text-xs text-muted", "data-daily-series-note": "true" },
      "Calculada a partir dos pedidos, pelo dia de criação (horário de São Paulo). Só pedidos que valem venda somam receita; cancelados ficam de fora."
    )
  );
}

export interface DashboardExecutivoViewProps extends DashboardViewProps {
  /** Pedidos unificados; ausente = painéis declaram a fonte pendente. */
  orders?: OrdersDataset;
  /** Barra de período/atualização já montada e o período resolvido. */
  toolbar?: ReactElement;
  period?: ResolvedPeriod;
  /** Cadastro de origens de aquisição (rótulos dos cards de origem). */
  acquisitionOptions?: readonly SalesOriginOption[];
}

export function DashboardExecutivoView({ dataset, now, orders, toolbar, period, acquisitionOptions }: DashboardExecutivoViewProps): ReactElement {
  const unified = orders && !isOrdersSourceAwaitingContract(orders) ? orders.orders : undefined;
  const kpis = applyUnifiedOrderKpis(buildDashboardKpis(dataset, now ?? new Date()), unified);
  const executive = kpis.filter((kpi) => kpi.audience === "executivo" || kpi.audience === "ambos");
  const stats = buildCommandCenterStats(dataset);
  return h(
    "div",
    { className: "page page--dashboard-executivo flex flex-col gap-6", "data-page": "dashboard-executivo" },
    h(PageHeader, {
      title: "Visão geral",
      description: "Resultado, atendimento e pedidos de todos os canais em um só lugar."
    }),
    toolbar ?? null,
    h(KpiGrid, {
      kpis: executive,
      title: "Visão executiva",
      description:
        "Indicadores de resultado. Cada card navega até os registros que o compõem — nenhum número é um total cego."
    }),
    h(OrdersPanels, { orders }),
    unified ? h(AcquisitionLeaderCards, { orders: unified, options: acquisitionOptions }) : null,
    unified ? h(TopProductsPanel, { orders: unified }) : null,
    orders && !isOrdersSourceAwaitingContract(orders) ? h(DailySeriesPanel, { orders, now: now ?? new Date(), period }) : null,
    h(
      "section",
      {
        className: "command-center flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1",
        "aria-label": "Command Center"
      },
      h("h2", { className: "text-lg font-extrabold text-ink" }, "Command Center"),
      // Faixa de métricas separada por divisores (não quatro cards com sombra).
      h(
        "dl",
        {
          className:
            "command-center__stats m-0 grid grid-cols-2 gap-x-0 gap-y-3 " +
            "divide-y divide-border sm:grid-cols-3 sm:divide-y-0 lg:grid-cols-6"
        },
        stat("Conversas abertas", "openConversations", formatCount(stats.openConversations)),
        stat("Aguardando humano", "pendingHumanTakeover", formatCount(stats.pendingHumanTakeover)),
        stat("Lead score médio", "averageLeadScore", formatCount(stats.averageLeadScore)),
        stat("Resolução pela IA", "aiResolutionRatePercent", formatPercent(stats.aiResolutionRatePercent)),
        stat("Conversas no período", "conversationsLast24h", formatCount(stats.conversationsLast24h)),
        stat("Faturamento de pedidos pagos", "revenueTodayCents", formatBrl(unified ? revenueSummary(unified).revenueCents : stats.revenueTodayCents))
      ),
      // Indicador gráfico leve: reforça a hierarquia IA x humano. Os números já
      // aparecem em texto na faixa acima — o gráfico é reforço, não a fonte.
      h(
        "div",
        { className: "border-t border-border pt-4" },
        h("h3", { className: "mb-2 text-sm font-extrabold text-muted" }, "Distribuição de atendimento"),
        h(MiniBarChart, {
          title: "Distribuição de atendimento",
          "data-chart": "atendimento",
          bars: [
            {
              label: "Resolução pela IA",
              value: stats.aiResolutionRatePercent,
              display: formatPercent(stats.aiResolutionRatePercent)
            },
            {
              label: "Aguardando humano",
              value: stats.pendingHumanTakeover,
              display: formatCount(stats.pendingHumanTakeover),
              emphasis: stats.pendingHumanTakeover > 0
            }
          ]
        })
      )
    )
  );
}

export function DashboardExecutivo(): ReactElement {
  const origins = useSalesOrigins();
  return h(DatasetBoundary, {
    children: (dataset: DashboardDataset) =>
      // Padrão: MÊS ATUAL. Os cartões de pedido/receita e os painéis seguem o período; os de atendimento são do momento.
      h(PeriodOrdersBoundary, {
        defaultPeriod: "mes",
        children: (orders: OrdersDataset, context) => h(DashboardExecutivoView, { dataset, orders, toolbar: context.toolbar, period: context.period, acquisitionOptions: origins.data })
      })
  });
}
